import { type FavoriteWorkplacesResponse } from "@clipboard-health/contract-worker-app-bff";
import {
  Checkbox,
  DotSeparatedList,
  ListItem,
  ListItemIcon,
  ListItemText,
  Text,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { type ListItemProps } from "@mui/material";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import {
  getAddFavoriteWorkplaceErrorMessage,
  useAddFavoriteWorkplace,
} from "@src/appV2/Workplaces/api/useAddFavoriteWorkplace";
import { getFavoriteWorkplacesQueryKey } from "@src/appV2/Workplaces/api/useGetFavoriteWorkplaces";
import { useRemoveFavoriteWorkplace } from "@src/appV2/Workplaces/api/useRemoveFavoriteWorkplace";
import { useQueryClient } from "@tanstack/react-query";
import { round } from "lodash";
import pluralize from "pluralize";
import { useCallback } from "react";

import { type UpdateFavoriteWorkplacesAnalytics } from "./types";
import { refetchFavoriteWorkplacesShiftsData } from "./useFavoriteWorkplacesShiftsData";
import { type WorkplaceGroupItem } from "./useWorkplacesSelection";

// Generate optimistic favorite ID using workerId::workplaceId pattern
function generateOptimisticFavoriteId(workerId: string, workplaceId: string): string {
  return `${workerId}::${workplaceId}`;
}

function formatDistance(distanceInMiles: number): string {
  if (distanceInMiles < 1) {
    return "<1 mile from home";
  }

  const roundedDistance = round(distanceInMiles, 1);
  return `${roundedDistance} ${pluralize("mile", roundedDistance)} from home`;
}

type WorkplaceItemProps = {
  item: WorkplaceGroupItem;
  isFavorite: boolean;
  favoriteId?: string;
  favoriteWorkplacesCount: number;
  maxFavoriteCount: number;
  analytics: UpdateFavoriteWorkplacesAnalytics;
} & ListItemProps;

export function WorkplaceItem({
  item,
  isFavorite,
  favoriteId,
  favoriteWorkplacesCount,
  maxFavoriteCount,
  analytics,
  ...otherProps
}: WorkplaceItemProps) {
  const worker = useDefinedWorker();
  const queryClient = useQueryClient();
  const { showErrorToast } = useToast();

  // Individual mutations for this workplace
  const addFavoriteWorkplace = useAddFavoriteWorkplace({
    onMutate: async (params) => {
      const queryKey = getFavoriteWorkplacesQueryKey({ queryParams: {} });
      const snapshot = queryClient.getQueryData(queryKey);

      await queryClient.cancelQueries({ queryKey });

      queryClient.setQueryData<FavoriteWorkplacesResponse>(queryKey, (state) => {
        if (!isDefined(state)) {
          return state;
        }

        // Create optimistic favorite with workerId::workplaceId pattern
        const favorite = {
          type: "favorite-workplace" as const,
          id: generateOptimisticFavoriteId(params.workerId, params.workplaceId),
          attributes: {
            openShiftCount: item.openShiftCount ?? 0,
            distanceFromWorker: item.distanceFromWorker,
          },
          relationships: {
            workplace: {
              data: {
                type: "workplace" as const,
                id: params.workplaceId,
              },
            },
          },
        };

        // If favorite workplace already exists we don't need to add it again
        // We don't have the actual id for favorite we need to filter using worker and workplace id
        if (
          state.data.some(
            (item) =>
              item.relationships.workplace.data.id === favorite.relationships.workplace.data.id
          )
        ) {
          return state;
        }

        return {
          ...state,
          data: [...state.data, favorite],
          included: [
            ...(state.included ?? []),
            {
              type: "workplace" as const,
              id: params.workplaceId,
              attributes: {
                name: item.workplaceName,
                location: { longitude: 0, latitude: 0 }, // This will be populated after full invalidation
              },
            },
          ],
        };
      });

      return snapshot;
    },
    onSuccess: async (_, params) => {
      logEvent(APP_V2_USER_EVENTS.UPDATE_FAVORITE_WORKPLACES, {
        workplaceId: params.workplaceId,
        type: "add",
        // Passing and consuming meta from params instead of directly from component props
        // to capture the actual values at mutation time rather than changed values when success handler is called
        mode: params.analytics.mode,
        group: params.analytics.group,
      });
      // We want loader state to be shown so we use reset instead of invalidate
      await refetchFavoriteWorkplacesShiftsData(queryClient);
    },
    onError: (error, __, snapshot) => {
      showErrorToast(getAddFavoriteWorkplaceErrorMessage(error));
      queryClient.setQueryData(getFavoriteWorkplacesQueryKey({ queryParams: {} }), snapshot);
    },
  });

  const removeFavoriteWorkplace = useRemoveFavoriteWorkplace({
    onMutate: async (params) => {
      const queryKey = getFavoriteWorkplacesQueryKey({
        queryParams: {},
      });
      const snapshot = queryClient.getQueryData(queryKey);

      await queryClient.cancelQueries({ queryKey });

      queryClient.setQueryData<FavoriteWorkplacesResponse>(queryKey, (state) => {
        if (!isDefined(state)) {
          return state;
        }

        // Clear the favorite and related workplace from the cache
        const favorite = state.data.find((item) => item.id === params.favoriteId);
        if (isDefined(favorite)) {
          return {
            ...state,
            data: state.data.filter((item) => item.id !== favorite.id),
            included: state.included?.filter(
              (item) =>
                item.type === "workplace" && item.id !== favorite.relationships.workplace.data.id
            ),
          };
        }

        return state;
      });

      return snapshot;
    },
    onError: (_, __, snapshot) => {
      queryClient.setQueryData(getFavoriteWorkplacesQueryKey({ queryParams: {} }), snapshot);
    },
    onSuccess: async (_, params) => {
      logEvent(APP_V2_USER_EVENTS.UPDATE_FAVORITE_WORKPLACES, {
        workplaceId: params.workplaceId,
        type: "remove",
        mode: params.analytics.mode,
        group: params.analytics.group,
      });

      // We want loader state to be shown so we use reset instead of invalidate
      await refetchFavoriteWorkplacesShiftsData(queryClient);
    },
  });

  // Check if this specific workplace is being mutated
  const isLoading = addFavoriteWorkplace.isLoading || removeFavoriteWorkplace.isLoading;

  // Check if the favorite ID matches the exact optimistic ID pattern
  const isOptimistic = favoriteId === generateOptimisticFavoriteId(worker.userId, item.workplaceId);
  const isDisabled = isLoading || isOptimistic;

  const handleToggleFavorite = useCallback(() => {
    // We don't want to toggle favorite if the list is being mutated
    // To avoid race condition in UX
    if (isDisabled) {
      return;
    }

    if (!isDefined(worker?.userId)) {
      return;
    }

    // Favorite ID should always be defined if the workplace is a favorite
    if (isFavorite && isDefined(favoriteId)) {
      removeFavoriteWorkplace.mutate({
        favoriteId,
        workplaceId: item.workplaceId,
        analytics,
      });
    } else {
      // Check if max favorites limit is reached before adding
      if (favoriteWorkplacesCount >= maxFavoriteCount) {
        showErrorToast(`You can only choose up to ${maxFavoriteCount} favorite workplaces`);
        return;
      }

      addFavoriteWorkplace.mutate({
        workerId: worker.userId,
        workplaceId: item.workplaceId,
        analytics,
      });
    }
  }, [
    worker.userId,
    isDisabled,
    isFavorite,
    favoriteId,
    removeFavoriteWorkplace,
    favoriteWorkplacesCount,
    maxFavoriteCount,
    addFavoriteWorkplace,
    item.workplaceId,
    showErrorToast,
    analytics,
  ]);

  return (
    <ListItem sx={{ py: 3 }} {...otherProps}>
      <ListItemIcon sx={{ minWidth: "auto", mr: 4 }}>
        <Checkbox checked={isFavorite} onChange={handleToggleFavorite} />
      </ListItemIcon>
      <ListItemText
        disableTypography
        primary={
          <Text
            variant="body1"
            sx={{
              fontWeight: "medium",
              color: (theme) => theme.palette.text.primary,
              lineHeight: 1.2,
            }}
          >
            {item.workplaceName}
          </Text>
        }
        secondary={
          <DotSeparatedList inline={false}>
            {isDefined(item.openShiftCount) && (
              <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
                {item.openShiftCount === 0
                  ? "Currently no open shifts"
                  : `${item.openShiftCount} ${pluralize("shift", item.openShiftCount)}`}
              </Text>
            )}
            {isDefined(item.distanceFromWorker) && (
              <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
                {formatDistance(item.distanceFromWorker)}
              </Text>
            )}
          </DotSeparatedList>
        }
      />
    </ListItem>
  );
}
